'use client';

import { type FC, type ReactNode, useMemo } from 'react';
import { cn } from '../utils/cn';
import { SkinImage } from './ui/SkinImage';
import { Button } from './ui/Button';
import { PrizeNamePriceRow } from './PrizeNamePriceRow';
import { RarityGlowOverlay } from './RarityGlowOverlay';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { rarityGradient } from '../utils/rarityGradient';

interface PrizeCardShellProps {
  prize: {
    hashName: string;
    name: string;
    imageUrl?: string | null;
    rarityColor?: string | null;
  };
  credits: number;
  steamPrice?: number | null;
  priceCaption?: ReactNode;
  showQualityBadges?: boolean;

  topSlot?: ReactNode;
  topSlotPlacement?: 'float' | 'flow';

  statsSlot?: ReactNode;

  footerLabel?: ReactNode;
  onFooterClick?: (e: React.MouseEvent) => void;
  footerDisabled?: boolean;
  footerAboveSlot?: ReactNode;

  onCardClick?: () => void;
  onImageClick?: () => void;

  className?: string;
}

export const PrizeCardShell: FC<PrizeCardShellProps> = ({
  prize,
  credits,
  steamPrice,
  priceCaption,
  showQualityBadges,
  topSlot,
  topSlotPlacement = 'float',
  statsSlot,
  footerLabel,
  onFooterClick,
  footerDisabled,
  footerAboveSlot,
  onCardClick,
  onImageClick,
  className = '',
}) => {
  const isDesktop = useIsDesktop();
  const cardStyle = useMemo(() => ({ background: rarityGradient(prize.rarityColor) }), [prize.rarityColor]);

  const image = (
    <SkinImage name={prize.name} src={prize.imageUrl} imgClassName="w-full h-full object-contain" />
  );

  const namePrice = (layout: 'mobile' | 'desktop', extra?: string) => (
    <PrizeNamePriceRow
      hashName={prize.hashName}
      fallbackName={prize.name}
      credits={credits}
      steamPrice={steamPrice}
      layout={layout}
      priceCaption={layout === 'desktop' ? priceCaption : undefined}
      showQualityBadges={showQualityBadges}
      className={extra}
    />
  );

  if (isDesktop) {
    return (
      <div
        className={cn(
          'group relative flex w-full flex-row overflow-hidden rounded-[21px] text-white',
          onCardClick && 'cursor-pointer',
          className,
        )}
        style={cardStyle}
        onClick={onCardClick}
      >
        <RarityGlowOverlay />
        <div className="relative z-[1] flex min-w-0 flex-1 flex-col gap-4 p-4">
          {topSlot}
          {namePrice('desktop')}
          {statsSlot}
          {footerLabel && (
            <div className="mt-auto">
              {footerAboveSlot}
              <Button
                type="button"
                variant="white"
                size="m"
                stretched
                onClick={onFooterClick}
                disabled={footerDisabled}
              >
                {footerLabel}
              </Button>
            </div>
          )}
        </div>

        <ImageArea onClick={onImageClick} className="relative flex w-[440px] shrink-0 items-center justify-center p-2">
          <div
            className="relative flex h-full w-full items-center justify-center transition-transform duration-300 group-hover:scale-105"
            style={{ filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.55))' }}
          >
            {image}
          </div>
        </ImageArea>
      </div>
    );
  }

  const floating = topSlotPlacement === 'float';

  return (
    <div
      className={cn(
        'relative flex w-full flex-col overflow-hidden rounded-[21px] p-0 text-white',
        onCardClick && 'cursor-pointer',
        className,
      )}
      style={cardStyle}
      onClick={onCardClick}
    >
      <RarityGlowOverlay />
      {floating && topSlot}

      <div className="relative flex w-full flex-col">
        {!floating && topSlot}

        <ImageArea
          onClick={onImageClick}
          className={cn(
            'flex items-center justify-center px-4',
            floating ? 'h-[220px] pt-4' : 'h-[210px]',
          )}
          style={{ filter: 'drop-shadow(0 12px 20px rgba(0,0,0,0.55))' }}
        >
          {image}
        </ImageArea>

        {namePrice('mobile', 'mt-1 px-4')}

        {statsSlot && <div className="px-4 pb-2.5 pt-2">{statsSlot}</div>}

        {footerLabel && (
          <div className="mt-auto px-4 pb-4">
            {footerAboveSlot}
            <Button
              type="button"
              variant="white"
              size="l"
              stretched
              onClick={onFooterClick}
              disabled={footerDisabled}
              className="h-[45px]"
            >
              {footerLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const ImageArea: FC<{
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}> = ({ onClick, className, style, children }) => {
  if (!onClick) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} style={style}>
      {children}
    </button>
  );
};
